import {
  IAddEventAttachment,
  IAddEventLiveRecord,
  IAddLiveRecordView,
  IAddRemoveAttendee,
  IChangeEventCommentUsefulVote,
  IChangeEventMessageUsefulVote,
  ICreateEvent,
  IRemoveEvent,
  IRemoveEventAttachment,
  IRemoveEventComment,
  IRemoveEventLiveRecord,
  IRemoveEventMessage,
  IBulkRemoveEvents,
  IRemoveEventsResult,
  ISendComment,
  ISendEventMessage,
  IUpdateEvent,
  IUpdateEventLiveRecordName,
  IBulkEditLiveClassFormations
} from '@sdk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';

import { sdk } from '@/services';

const EventService = sdk.getService('EventService');

export const useMutateEvents = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['events'] });
    queryClient.invalidateQueries({ queryKey: ['events', 'teachers-with-stats'] });
    queryClient.refetchQueries({ queryKey: ['events', 'teachers-with-stats'] });
  };

  const { t } = useTranslation('CommonEventsTranslations');

  const { mutate: createEvent, isPending: isCreating } = useMutation({
    mutationFn: (event: ICreateEvent) => EventService.createEvent(event),
    onSuccess: () => {
      onSuccess();
      message.success(t('EVENT_CREATED_SUCCESSFULLY'));
    }
  });

  const { mutate: updateEvent, isPending: isUpdating } = useMutation({
    mutationFn: (update: IUpdateEvent) => EventService.updateEvent(update),
    onSuccess: () => {
      onSuccess();
      message.success(t('EVENT_UPDATED_SUCCESSFULLY'));
    }
  });

  const { mutate: removeEvent, isPending: isRemoving } = useMutation({
    mutationFn: async ({ eventId, removeRecurring }: IRemoveEvent) =>
      EventService.removeEvent(eventId, removeRecurring),

    onSuccess: (res: IRemoveEventsResult) => {
      onSuccess();
      if (res && Array.isArray(res.failed) && res.failed.length > 0) {
        const lines = res.failed.map(
          (f: { id: string; reason: string }) => `• ${f.id}: ${f.reason}`
        );
        message.error(lines.join('\n'));
      } else {
        message.success(t('EVENT_REMOVED_SUCCESSFULLY'));
      }
    }
  });

  const { mutate: addAttendee, isPending: isAddingAttendee } = useMutation({
    mutationFn: (add: IAddRemoveAttendee) => EventService.addAttendee(add),
    onSuccess
  });

  const { mutate: removeAttendee, isPending: isRemovingAttendee } = useMutation({
    mutationFn: (remove: IAddRemoveAttendee) => EventService.removeAttendee(remove),
    onSuccess
  });

  const { mutate: sendMessage, isPending: isSending } = useMutation({
    mutationFn: (send: ISendEventMessage) => EventService.sendMessage(send),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      message.success(t('MESSAGE_SENT_CORRECTLY'));
    }
  });
  const { mutate: removeMessage, isPending: isRemovingMessage } = useMutation({
    mutationFn: (remove: IRemoveEventMessage) => EventService.removeMessage(remove),
    onSuccess: () => {
      onSuccess();
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      message.success(t('MESSAGE_REMOVED_CORRECTLY'));
    }
  });
  const { mutate: removeComment, isPending: isRemovingComment } = useMutation({
    mutationFn: (remove: IRemoveEventComment) => EventService.removeComment(remove),
    onSuccess: () => {
      onSuccess();
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      message.success(t('MESSAGE_REMOVED_CORRECTLY'));
    }
  });

  const { mutate: changeUsefulMessageVote, isPending: isChangingUsefulMessageVote } = useMutation({
    mutationFn: (change: IChangeEventMessageUsefulVote) =>
      EventService.changeUsefulMessageVote(change),
    onSuccess: () => {
      onSuccess();
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    }
  });
  const { mutate: changeUsefulCommentVote, isPending: isChangingUsefulCommentVote } = useMutation({
    mutationFn: (change: IChangeEventCommentUsefulVote) =>
      EventService.changeUsefulCommentVote(change),
    onSuccess: () => {
      onSuccess();
      queryClient.invalidateQueries({ queryKey: ['comments'] });
    }
  });

  const { mutate: sendComment, isPending: isSendingComment } = useMutation({
    mutationFn: (commentSend: ISendComment) => EventService.sendComment(commentSend),
    onSuccess: () => {
      onSuccess();
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      message.success(t('COMMENT_SENT_CORRECTLY'));
    }
  });

  const { mutate: removeAttachment, isPending: isRemovingAttachment } = useMutation({
    mutationFn: (remove: IRemoveEventAttachment) => EventService.removeAttachment(remove),
    onSuccess: () => {
      onSuccess();
      message.success(t('ATTACHMENT_REMOVED_CORRECTLY'));
    }
  });

  const { mutate: addAttachment, isPending: isAddingAttachment } = useMutation({
    mutationFn: (add: IAddEventAttachment) => EventService.addAttachment(add),
    onSuccess: () => {
      onSuccess();
      message.success(t('ATTACHMENT_ADDED_CORRECTLY'));
    }
  });

  const { mutate: removeLiveRecord, isPending: isRemovingLiveRecord } = useMutation({
    mutationFn: (remove: IRemoveEventLiveRecord) => EventService.removeLiveRecord(remove),
    onSuccess: () => {
      onSuccess();
      message.success(t('ATTACHMENT_REMOVED_CORRECTLY'));
    }
  });

  const { mutate: addLiveRecord, isPending: isAddingLiveRecord } = useMutation({
    mutationFn: (add: IAddEventLiveRecord) => EventService.addLiveRecord(add),
    onSuccess: () => {
      onSuccess();
      message.success(t('ATTACHMENT_ADDED_CORRECTLY'));
    }
  });

  const { mutate: updateLiveRecordName, isPending: isUpdatingLiveRecordName } = useMutation({
    mutationFn: (update: IUpdateEventLiveRecordName) => EventService.updateLiveRecordName(update),
    onSuccess: () => {
      onSuccess();
      message.success(t('LIVE_RECORD_NAME_UPDATED_CORRECTLY'));
    }
  });

  const { mutate: removeEventsInBulk, isPending: isBulkRemoving } = useMutation({
    mutationFn: (payload: IBulkRemoveEvents) => EventService.removeEventsInBulk(payload.ids),
    onSuccess: (res: IRemoveEventsResult) => {
      onSuccess();
      if (res && Array.isArray(res.failed) && res.failed.length > 0) {
        const lines = res.failed.map(f => `• ${f.id}: ${f.reason}`);
        message.error(lines.join('\n'));
      } else {
        message.success(t('EVENTS_REMOVED_SUCCESSFULLY'));
      }
    }
  });

  const { mutate: addLiveRecordView } = useMutation({
    mutationFn: (add: IAddLiveRecordView) => EventService.addLiveRecordView(add),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events', 'live-records'] });
    }
  });

  const { mutate: editLiveClassInBulk, isPending: isBulkEditing } = useMutation({
    mutationFn: (payload: IBulkEditLiveClassFormations) =>
      EventService.editLiveClassInBulk(payload),
    onSuccess: () => {
      onSuccess();
      message.success(t('CHANGES_SAVED_SUCCESSFULLY'));
    }
  });

  return {
    createEvent,
    updateEvent,
    removeEvent,
    addAttendee,
    removeAttendee,
    sendMessage,
    removeMessage,
    sendComment,
    removeComment,
    changeUsefulMessageVote,
    changeUsefulCommentVote,
    removeAttachment,
    addAttachment,
    removeLiveRecord,
    addLiveRecord,
    updateLiveRecordName,
    addLiveRecordView,
    removeEventsInBulk,
    editLiveClassInBulk,
    isWorking:
      isCreating ||
      isUpdating ||
      isRemoving ||
      isBulkRemoving ||
      isBulkEditing ||
      isAddingAttendee ||
      isRemovingAttendee ||
      isSending ||
      isRemovingMessage ||
      isRemovingAttachment ||
      isAddingAttachment ||
      isRemovingLiveRecord ||
      isAddingLiveRecord ||
      isUpdatingLiveRecordName ||
      isChangingUsefulMessageVote ||
      isSendingComment ||
      isRemovingComment ||
      isChangingUsefulCommentVote
  };
};
